import React, { FC } from 'react';
import {
  BasicContainer,
  BodyText,
  DividerWithLabel,
  FlexCol,
  FlexDynamic,
  FlexRow,
  TextDynamic,
} from '@minecraft.atoms';
import { PlanTermEnum } from '@minecraft.graphql-operations';
import { useTranslation } from '@blocs.i18n';
import { WindowWidthContext } from '@minecraft.utils';
import Icon from '@minecraft.icon';
import { gqlRequestMediaType } from '@minecraft.graphql-types';
import { PlanType, PLANS } from '../usePlans';
import { BillingCodes, MediaPurchase, TemperatureValues } from '../enums/plans';
import { PlanSelectionProps } from '../PlanSelection/types';
import { PaywallLocation } from '../enums/PaywallLocation';
import { CollapsiblePurchase } from './CollapsiblePurchase';
import { FeatureList } from '../PlanSelection/FeatureList';
import { CheckoutProps } from './types';
import { VerticalPlanLoader } from './VerticalPlansLoader';
import { priceFormatter } from '../helpers/priceFormatter';
import { TestimonialCarousel } from '../PlanSelection/TestimonialCarousel';

export interface VerticalPlansProps extends PlanSelectionProps {
  currentPlanCode: BillingCodes;
  paywallLocation?: PaywallLocation;
  isWithinTrialPeriod?: boolean;
  previewMediaPurchase?: CheckoutProps['previewMediaPurchase'];
  previewMediaPurchaseLoading?: CheckoutProps['previewMediaPurchaseLoading'];
}

export const VerticalPlans: FC<VerticalPlansProps> = React.memo(
  ({
    plans,
    countryCode,
    setSelectedPlan,
    selectedPlan,
    currentPlanCode,
    paywallLocation,
    isAnnual,
    isWithinTrialPeriod,
    previewMediaPurchase,
    previewMediaPurchaseLoading,
  }) => {
    const { t } = useTranslation();
    const isMobile = WindowWidthContext.useIsMobile();
    const sortedPlans = plans.slice().sort((a, b) => b.order - a.order); // Premium should be first
    const filesToPreview = previewMediaPurchase?.items?.map((item) => ({
      fileName: item.media?.filename,
      filePrice: item.price,
      id: item.media.id,
      fileThumbnailSrc: item.media?.thumbnailUrl,
      mediaType: item.media?.mediaType,
    }));
    const singleItemPrice = filesToPreview?.find((item) => item.filePrice > 0)?.filePrice;

    const isShown = (plan: PlanType): boolean => {
      const isRegistration = plan.billingCode === BillingCodes.registration;
      const currentPlan = PLANS.find((plan) => plan.billingCode === currentPlanCode);
      const isValidPlan = plan.code === currentPlan?.code && plan.term.code === PlanTermEnum.Monthly && plan.isCurrent;
      const hasActiveTrialPlan = plan.trialLengthDays > 0 && isWithinTrialPeriod;

      return (
        !plan.shouldHidePlan &&
        !isRegistration &&
        !hasActiveTrialPlan &&
        (plan.canUpgrade || isValidPlan || plan.canRenew)
      );
    };

    return (
      <FlexDynamic
        direction="column"
        gap="4"
        mdProps={{ direction: 'column', gap: '4' }}
        wrap="nowrap"
        justifyContent="flex-start"
        w="full"
      >
        {sortedPlans.map((plan) => {
          if (!isShown(plan)) return null;

          return (
            <CollapsiblePurchase
              temperature={plan.temperature}
              isDisabled={plan.isCurrent && !plan.canRenew}
              key={plan?.billingCode}
              countryCode={countryCode}
              plan={plan}
              setSelectedPlan={setSelectedPlan}
              selectedPlan={selectedPlan}
              paywallLocation={paywallLocation}
              isAnnual={isAnnual}
              isTrialPeriodEnabled={plan.isTrialPeriodEnabled}
              billed={plan?.billed}
              currencyCode={plan?.currencyCode}
              planTermCode={plan.term?.code}
              price={Number(plan?.price)}
              discountedPrice={Number(plan?.discountedPrice)}
              title={plan.title}
              icon={plan.icon}
              heroImage={isMobile ? null : plan.heroImage}
              billingCode={plan?.billingCode}
            >
              <FlexCol w="full" className="cn_atom_px-2 cn_h-full" justifyContent="flex-start" gap="2" mt="4">
                <FeatureList
                  temperature={plan.temperature}
                  iconName={plan.iconName || 'check-circle'}
                  features={plan.isTrialPeriodEnabled && plan.isEssentialTrialPlan ? plan.keyFeatures : plan.features}
                  isFullFeatureList
                />
              </FlexCol>
            </CollapsiblePurchase>
          );
        })}
        {!isMobile && <TestimonialCarousel />}
        {(filesToPreview || previewMediaPurchaseLoading) && (
          <>
            <DividerWithLabel label={t('common:paywall.card.or')} />
            <FlexCol w="full" data-qa-id="media-price-preview-pane" gap="6">
              <TextDynamic
                as="h2"
                fontSize="h2"
                align="left"
                fontWeight="medium"
                color="default"
                mdProps={{ fontWeight: 'semibold' }}
              >
                {t('common:paywall.purchaseMedia.title')}
              </TextDynamic>
              {previewMediaPurchaseLoading && !filesToPreview ? (
                <VerticalPlanLoader />
              ) : (
                <CollapsiblePurchase
                  temperature={TemperatureValues.vanilla}
                  countryCode={countryCode}
                  setSelectedPlan={setSelectedPlan}
                  selectedPlan={selectedPlan}
                  paywallLocation={paywallLocation}
                  isAnnual={isAnnual}
                  billed="media-purchase"
                  currencyCode={previewMediaPurchase?.currency?.code}
                  price={Number(singleItemPrice ?? 0)}
                  title={t('common:paywall.purchaseMedia.item')}
                  heroImage={isMobile ? null : 'purchase-media'}
                  billingCode={MediaPurchase.alacarte}
                  isMediaPurchase
                >
                  <FlexCol w="full" className="cn_atom_px-2 cn_h-full" justifyContent="flex-start" gap="2" mt="4">
                    <FlexRow
                      w="full"
                      className="cn_atom_px-3"
                      justifyContent="center"
                      data-qa-id="media-price-preview-pane"
                    >
                      {filesToPreview?.map(({ fileName, filePrice, fileThumbnailSrc, mediaType }, i) => {
                        const isLast = i === filesToPreview.length - 1;
                        const mediaPrice = priceFormatter({
                          price: Number(filePrice),
                          currencyCode: previewMediaPurchase.currency.code,
                          countryCode,
                          t,
                        });
                        const price = filePrice === 0 ? t('common:paywall.plan.registration.title') : mediaPrice;

                        return (
                          <FlexRow
                            w="full"
                            key={fileName}
                            className="cn_atom_py-4"
                            gap="2"
                            justifyContent="space-between"
                            alignItems="center"
                            borderStyle="solid"
                            borderPlacement={isLast ? 'none' : 'bottom'}
                            wrap="nowrap"
                          >
                            <FlexRow w="fit" gap="4" alignItems="center">
                              <BasicContainer className="cn_w-fit cn_atom_rounded-sm cn_shadow-md">
                                {mediaType?.code === gqlRequestMediaType.AUDIO ? (
                                  <Icon name="audio" />
                                ) : (
                                  <img
                                    src={fileThumbnailSrc}
                                    alt={fileName}
                                    className="cn_w-12 cn_h-14 cn_object-cover"
                                  />
                                )}
                              </BasicContainer>
                              <FlexDynamic w="fit" maxW="40" className="cn_truncate" mdProps={{ maxW: 'fit' }}>
                                <BodyText color="disabled" fontWeight="medium" align="center">
                                  {fileName}
                                </BodyText>
                              </FlexDynamic>
                            </FlexRow>
                            <BodyText as="h4" color="disabled" fontWeight="bold" align="center">
                              {price}
                            </BodyText>
                          </FlexRow>
                        );
                      })}
                    </FlexRow>
                  </FlexCol>
                </CollapsiblePurchase>
              )}
            </FlexCol>
          </>
        )}
      </FlexDynamic>
    );
  }
);
